import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, takeLatest } from "redux-saga/effects";
import { facilitiesApi } from "../../services/facilities.api";
import { Facility, FacilityUpdate } from "../../types/facility.definition";
import {
  fetchFacilityFailure,
  fetchFacilityRequest,
  fetchFacilitySuccess,
  updateFacilityFailure,
  updateFacilityRequest,
  updateFacilitySuccess,
} from "../reducers/facilitySlice";
import { getSessionRequest } from "../reducers/sessionSlice";

function normalizeHours(facility: Facility): Facility {
  if (!facility.hours) return facility;

  const hours = Array.isArray(facility.hours)
    ? facility.hours.reduce(
        (acc, dayHours) => {
          const day = Object.keys(dayHours)[0];
          acc[day] = dayHours[day];
          return acc;
        },
        {} as { [key: string]: { open: string; close: string } }
      )
    : facility.hours;

  return { ...facility, hours };
}

function* fetchFacilitySaga({
  payload,
}: PayloadAction<{ facilityId: string | number }>): Generator<
  unknown,
  void,
  Facility
> {
  try {
    const facility = yield call(
      facilitiesApi.getFacilityById,
      payload.facilityId
    );
    yield put(
      fetchFacilitySuccess({ facility: normalizeHours(facility) })
    );
  } catch (error) {
    yield put(
      fetchFacilityFailure(
        error instanceof Error ? error.message : "Failed to fetch facility"
      )
    );
  }
}

function* updateFacilitySaga({
  payload,
}: PayloadAction<{
  facilityId: number;
  data: Partial<FacilityUpdate>;
}>): Generator<unknown, void, Facility> {
  try {
    const updatedFacility = yield call(
      facilitiesApi.updateFacility,
      payload.facilityId,
      payload.data
    );

    yield put(
      updateFacilitySuccess({
        facility: normalizeHours(updatedFacility),
      })
    );
    // Re-fetch to get fully populated data (e.g. specialties with labels)
    yield put(fetchFacilityRequest({ facilityId: payload.facilityId }));
    // Sync session store so manager.healthcareFacility stays up to date
    yield put(getSessionRequest());
  } catch (error) {
    yield put(
      updateFacilityFailure(
        error instanceof Error ? error.message : "Failed to update facility"
      )
    );
  }
}

export function* watchFacility() {
  yield takeLatest(fetchFacilityRequest.type, fetchFacilitySaga);
  yield takeLatest(updateFacilityRequest.type, updateFacilitySaga);
}
